/**
 * 环境变量管理器
 * 提供类型安全的环境变量访问和配置管理
 */

// import { throwCasaApiError } from "@/utils/errorHandler";
import { captureException } from "@/services/sentry";

// 环境类型定义
export type Environment = "development" | "staging" | "production";

// 配置接口定义
export interface ApiConfig {
  baseUrl: string;
  prefix: string;
}

export interface ExternalServices {
  googleAnalytics: {
    id: string;
    enabled: boolean;
  };
  sentry: {
    dsn: string;
    enabled: boolean;
  };
  whatsAppLink: string;
}

export interface FeatureFlags {
  enableMSW: boolean;
  enableAnalytics: boolean;
  enableSentry: boolean;
  devMode: boolean;
}

export interface AppConfig {
  name: string;
  version: string;
  basePath: string;
  environment: Environment;
}

export interface EnvConfig {
  api: ApiConfig;
  services: ExternalServices;
  features: FeatureFlags;
  app: AppConfig;
}

// 环境变量解析工具
class EnvManager {
  private config: EnvConfig;
  private currentMarket: string | undefined;

  constructor() {
    this.config = this.parseEnvVariables();
    this.validateConfig();
  }

  /**
   * 解析环境变量为配置对象
   */
  private parseEnvVariables(): EnvConfig {
    const env = import.meta.env;

    return {
      api: {
        baseUrl: this.getRequiredEnv("VITE_API_BASE_URL"),
        prefix: this.getRequiredEnv("VITE_API_PREFIX"),
      },
      services: {
        googleAnalytics: {
          id: this.getRequiredEnv("VITE_GOOGLE_ANALYTICS_ID"),
          enabled: this.getBooleanEnv("VITE_ENABLE_ANALYTICS", false),
        },
        sentry: {
          dsn: this.getRequiredEnv("VITE_SENTRY_DSN"),
          enabled: this.getBooleanEnv("VITE_ENABLE_SENTRY", false),
        },
        whatsAppLink: this.getRequiredEnv("VITE_WHATSAPP_LINK"),
      },
      features: {
        enableMSW: this.getBooleanEnv("VITE_ENABLE_MSW", false),
        enableAnalytics: this.getBooleanEnv("VITE_ENABLE_ANALYTICS", false),
        enableSentry: this.getBooleanEnv("VITE_ENABLE_SENTRY", false),
        devMode: this.getBooleanEnv("VITE_DEV_MODE", env.DEV),
      },
      app: {
        name: this.getEnv("VITE_APP_NAME", "PSA Chat"),
        version: this.getEnv("VITE_APP_VERSION", "0.0.1"),
        basePath: this.getEnv("VITE_BASE_PATH", "/"),
        environment: this.getEnvironment(),
      },
    };
  }

  /**
   * 获取必需的环境变量
   */
  private getRequiredEnv(key: keyof ImportMetaEnv): string {
    const value = import.meta.env[key];
    if (!value) {
      throw new Error(`Required environment variable ${key} is not defined`);
    }
    return value;
  }

  /**
   * 获取可选的环境变量
   */
  private getEnv(key: keyof ImportMetaEnv, defaultValue: string): string {
    return import.meta.env[key] || defaultValue;
  }

  /**
   * 获取布尔类型环境变量
   */
  private getBooleanEnv(
    key: keyof ImportMetaEnv,
    defaultValue: boolean
  ): boolean {
    const value = import.meta.env[key];
    if (value === undefined) return defaultValue;
    return value === "true" || value === "1";
  }

  /**
   * 获取数字类型环境变量
   */
  private getNumberEnv(key: keyof ImportMetaEnv, defaultValue: number): number {
    const value = import.meta.env[key];
    if (value === undefined) return defaultValue;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? defaultValue : parsed;
  }

  /**
   * 获取当前环境
   */
  private getEnvironment(): Environment {
    const nodeEnv = import.meta.env.NODE_ENV;
    const mode = import.meta.env.MODE;

    if (mode === "production" || nodeEnv === "production") {
      return "production";
    } else if (mode === "staging") {
      return "staging";
    } else {
      return "development";
    }
  }

  /**
   * 验证配置的有效性
   */
  private validateConfig(): void {
    const { api, services } = this.config;

    // 验证 API URL 格式
    try {
      new URL(api.baseUrl);
    } catch {
      throw new Error(`Invalid API base URL: ${api.baseUrl}`);
    }

    // 在生产环境中验证必要的服务配置
    if (this.isProduction()) {
      if (
        services.sentry.enabled &&
        !services.sentry.dsn.startsWith("https://")
      ) {
        console.warn("Sentry DSN should be a valid HTTPS URL in production");
      }
    }
  }

  /**
   * 获取完整配置
   */
  public getConfig(): EnvConfig {
    return { ...this.config };
  }

  /**
   * 获取 API 配置
   */
  public getApiConfig(): ApiConfig {
    return { ...this.config.api };
  }

  /**
   * 获取外部服务配置
   */
  public getServicesConfig(): ExternalServices {
    return { ...this.config.services };
  }

  /**
   * 获取功能开关配置
   */
  public getFeatureFlags(): FeatureFlags {
    return { ...this.config.features };
  }

  /**
   * 获取应用配置
   */
  public getAppConfig(): AppConfig {
    return { ...this.config.app };
  }

  /**
   * 检查是否为开发环境
   */
  public isDevelopment(): boolean {
    return this.config.app.environment === "development";
  }

  /**
   * 检查是否为测试环境
   */
  public isStaging(): boolean {
    return this.config.app.environment === "staging";
  }

  /**
   * 检查是否为生产环境
   */
  public isProduction(): boolean {
    return this.config.app.environment === "production";
  }

  /**
   * 检查功能是否启用
   */
  public isFeatureEnabled(feature: keyof FeatureFlags): boolean {
    return this.config.features[feature];
  }

  /**
   * 设置当前市场
   */
  public setCurrentMarket(market: string): void {
    this.currentMarket = market;
    console.log(`Market set to: ${market}`);
  }

  /**
   * 获取当前市场
   */
  public getCurrentMarket(): string | undefined {
    if (!this.currentMarket) {
      captureException(new Error("Current market not set"), {
        tags: {
          source: "envManager",
        },
      });
      // throwCasaApiError(new Error("Current market not set"), "[envManager] Current market not set");
      return undefined;
    }
    return this.currentMarket;
  }

  /**
   * 获取完整的 API URL
   */
  public getApiUrl(endpoint?: string): string {
    const { baseUrl, prefix } = this.config.api;

    // 在开发环境使用代理
    if (import.meta.env.DEV) {
      const fullUrl = "/api";
      return endpoint ? `${fullUrl}${endpoint}` : fullUrl;
    }

    // 在生产/测试环境，根据 market 动态构建 URL
    let finalBaseUrl = baseUrl;

    if (this.currentMarket) {
      // 将 market 转换为小写
      const marketLower = this.currentMarket.toLowerCase();

      // 替换 URL 中的市场标识
      // 例如: https://apigw-sg-test.castlery.com -> https://apigw-us-test.castlery.com
      finalBaseUrl = baseUrl.replace(
        /apigw-[a-z]{2}-(test|prod)/i,
        `apigw-${marketLower}-$1`
      );

      console.log(
        `API URL updated for market ${this.currentMarket}: ${finalBaseUrl}`
      );
    }

    const fullUrl = `${finalBaseUrl}${prefix}`;
    return endpoint ? `${fullUrl}${endpoint}` : fullUrl;
  }

  /**
   * 打印当前配置信息（开发环境）
   */
  public logConfig(): void {
    if (this.isDevelopment()) {
      console.group("🔧 Environment Configuration");
      console.log("Environment:", this.config.app.environment);
      console.log("API Base URL:", this.config.api.baseUrl);
      console.log("Feature Flags:", this.config.features);
      console.log("App Info:", {
        name: this.config.app.name,
        version: this.config.app.version,
      });
      console.groupEnd();
    }
  }
}

// 创建全局实例
export const envManager = new EnvManager();

// 导出常用的配置访问器
export const config = envManager.getConfig();
export const apiConfig = envManager.getApiConfig();
export const servicesConfig = envManager.getServicesConfig();
export const featureFlags = envManager.getFeatureFlags();
export const appConfig = envManager.getAppConfig();

// 导出环境检查函数
export const isDev = envManager.isDevelopment();
export const isStaging = envManager.isStaging();
export const isProd = envManager.isProduction();

// 在开发环境下打印配置信息
if (isDev) {
  envManager.logConfig();
}

export default envManager;
